import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { Dayreport } from 'src/app/classes/dayreport.class';
import { Planning } from 'src/app/classes/planning.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { DayreportService } from 'src/app/services/dayreport.service';
import { DayReportFieldGroups, EntityType, FieldType } from 'src/app/services/entities/entity-type.class';
import { EntityFormField } from 'src/app/services/entity-type.service';
import { WeatherService } from 'src/app/services/weather/weather.service';
import { LocalStorage } from 'src/app/storage.class';
import { entityTypes } from 'src/environments/trackline/entitytypes';
import { DagrapportStatus } from 'src/environments/trackline/status.dagrapport';
import { DayReportStateService } from '../day-report-state.service';

export interface DayReportFieldEntry {
    key: string;
    field: EntityFormField;
}

export interface DayReportFieldPair {
    label: string;
    entry1: DayReportFieldEntry;
    entry2: DayReportFieldEntry;
}

export interface DayReportFormGroup {
    key: string;
    label: string;
    fields: DayReportFieldEntry[];
    /** Populated when all fields come in _1/_2 pairs */
    pairs: DayReportFieldPair[] | null;
}

@Component({
    selector: 'app-day-report-form',
    standalone: false,
    templateUrl: './day-report-form.component.html',
    styleUrl: './day-report-form.component.scss',
})
export class DayReportFormComponent extends CodaltComponent implements OnInit {
    saving = false;
    saved = false;
    user = LocalStorage.getUser();

    private weatherService = inject(WeatherService);

    statusControl = new FormControl<string | null>(null);
    statusField: EntityFormField = {
        name: 'Status',
        type: FieldType.StatusKey,
        statusType: 'dagrapport',
        fc: this.statusControl,
    };

    commentControl = new FormControl<string | null>(null);
    commentField: EntityFormField = {
        name: 'Opmerking',
        type: FieldType.Textarea,
        fc: this.commentControl,
    };

    planning: Planning | null = null;
    dayReport: Dayreport | null = null;
    realisations: Realisation[] = [];

    groups: DayReportFormGroup[] = [];
    autofilledFCs = new Set<FormControl>();

    constructor(
        private stateService: DayReportStateService,
        private dayreportService: DayreportService,
        public location: Location,
        protected confirmDialog: ConfirmDialogService
    ) {
        super();
    }

    ngOnInit(): void {
        this.weatherService.prefetchLocation();

        this.subscriptions.add(
            this.stateService.planning$.subscribe(planning => {
                this.planning = planning;
                const entityTypeKey = planning?.mainPlanning?.entitytype_key;
                const entityType = entityTypeKey
                    ? (entityTypes[entityTypeKey as keyof typeof entityTypes] as EntityType)
                    : null;
                const dayReportFields = entityType?.fields_dayreport;
                if (dayReportFields) {
                    this.groups = this.buildGroups(dayReportFields);
                    this.setupWeatherAutofill();
                    this.setupThermoTijdAutofill();
                }
                this.applyReadOnlyState();
            })
        );

        this.subscriptions.add(
            this.stateService.realisations$.subscribe(realisations => {
                this.realisations = realisations;
            })
        );

        // Moet vóór de dayReport$-subscribe hieronder staan: die zet statusControl's waarde
        // (ook bij het eerste, synchrone bericht), en dat moet de required-validator op
        // commentControl direct meenemen.
        this.subscriptions.add(
            this.statusControl.valueChanges.subscribe(statusKey => this.applyCommentRequired(statusKey))
        );

        this.subscriptions.add(
            this.stateService.dayReport$.subscribe(dayReport => {
                if (!dayReport) {
                    // Nog geen dagrapport voor deze planning: alvast tonen wat het bij opslaan
                    // sowieso wordt (kolomdefault 'dagrapportGepland' op de dayreport-tabel).
                    this.statusControl.setValue('dagrapportGepland');
                    this.commentControl.setValue(null);

                    return;
                }
                this.dayReport = dayReport;
                this.statusControl.setValue(dayReport.status_key ?? null);
                this.commentControl.setValue(dayReport.comment ?? null, { emitEvent: false });
                const data = dayReport.form_data ?? {};
                for (const group of this.groups) {
                    for (const { key, field } of group.fields) {
                        if (data[key] !== undefined) {
                            field.fc.setValue(data[key], { emitEvent: false });
                        }
                    }
                }

                // Disable weather-autofilled fields if wegdek was already filled
                const tempGroup = this.groups.find(g => g.key === 'temp_vochtigheid');
                if (tempGroup) {
                    const getFC = (key: string) => tempGroup.fields.find(f => f.key === key)?.field.fc;
                    ([1, 2] as const).forEach(meting => {
                        if (getFC(`vocht_wegdek_${meting}`)?.value) {
                            [
                                `vocht_lucht_${meting}`,
                                `vocht_luchtvochtigheid_${meting}`,
                                `vocht_dauwpunt_${meting}`,
                                `vocht_tijd_${meting}`,
                            ].forEach(key => {
                                const fc = getFC(key);
                                if (fc) {
                                    fc.disable();
                                    this.autofilledFCs.add(fc);
                                }
                            });
                        }
                    });
                }

                // Disable thermo tijd if any of the triggers were filled and tijd has a value
                const thermoGroup = this.groups.find(g => g.key === 'temp_thermoplast');
                if (thermoGroup) {
                    const getFC = (key: string) => thermoGroup.fields.find(f => f.key === key)?.field.fc;
                    ([1, 2] as const).forEach(meting => {
                        const triggerKeys = [
                            `thermo_moederketel_${meting}`,
                            `thermo_machine_${meting}`,
                            `thermo_slof_${meting}`,
                        ];
                        const tijdFC = getFC(`thermo_tijd_${meting}`);
                        if (tijdFC?.value && triggerKeys.some(k => getFC(k)?.value)) {
                            tijdFC.disable();
                            this.autofilledFCs.add(tijdFC);
                        }
                    });
                }

                this.applyReadOnlyState();
            })
        );
    }

    get readOnly(): boolean {
        return this.dayReport?.status?.mutable !== true;
    }

    // Wanneer de verrekening (declaratie) van dit dagrapport al definitief is, mag de status
    // niet meer teruggezet worden - anders dan bij de gewone readOnly-staat blijft de
    // statusdropdown dan ook zelf disabled (die is normaal juist altijd bewerkbaar, zodat de
    // gebruiker de status kan wijzigen om weer bewerkbaar te worden).
    get declarationLocked(): boolean {
        return this.dayReport?.declaration?.status?.mutable === false;
    }

    private applyReadOnlyState(): void {
        this.groups
            .flatMap(group => group.fields.map(({ field }) => field.fc))
            .forEach(fc => {
                if (this.readOnly) {
                    fc.disable({ emitEvent: false });
                } else if (!this.autofilledFCs.has(fc)) {
                    fc.enable({ emitEvent: false });
                }
            });

        if (this.declarationLocked) {
            this.statusControl.disable({ emitEvent: false });
        } else {
            this.statusControl.enable({ emitEvent: false });
        }

        if (this.readOnly) {
            this.commentControl.disable({ emitEvent: false });
        } else {
            this.commentControl.enable({ emitEvent: false });
        }
    }

    private applyCommentRequired(statusKey: string | null): void {
        this.commentControl.setValidators(
            statusKey === DagrapportStatus.DagrapportGedeeltelijkAfgerond ? [Validators.required] : []
        );
        this.commentControl.updateValueAndValidity({ emitEvent: false });
    }

    get medewerkers(): (string | null)[] {
        const worktimeRealisations = this.realisations.filter(
            r => r.hourtype === RealisationHourtype.worktime && r.user_id
        );
        if (!worktimeRealisations.length) {
            return [];
        }
        return worktimeRealisations.map(r => {
            const begin = r.begindate ? new Date(r.begindate) : null;
            const end = r.enddate ? new Date(r.enddate) : null;
            const fmt = (d: Date) =>
                `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            const time = begin && end ? ` (${fmt(begin)}\u2013${fmt(end)})` : begin ? ` (${fmt(begin)})` : '';
            return r.user?.name ? `${r.user.name}${time}` : null;
        });
    }

    get entities() {
        const entityRealisations = this.realisations.filter(r => r.entity_id && !r.user_id);
        return entityRealisations;
    }

    openChangesBackActionCheck(): boolean | Promise<boolean> {
        if (!this.hasUnsavedChanges()) {
            return true;
        }

        return this.confirmDialog
            .confirm({
                title: 'Niet opgeslagen wijzigingen',
                text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                confirm: 'Hier blijven',
                cancel: 'Wijzigingen verwerpen',
            })
            .then(() => false)
            .catch(() => true);
    }

    protected hasUnsavedChanges(): boolean {
        return this.getFormControls().some(control => control.dirty);
    }

    private getFormControls(): FormControl[] {
        return [
            this.statusControl,
            this.commentControl,
            ...this.groups.flatMap(group => group.fields.map(({ field }) => field.fc)),
        ];
    }

    private markFormAsSaved(): void {
        this.getFormControls().forEach(control => {
            control.markAsPristine();
            control.markAsUntouched();
        });

        setTimeout(() => (this.saved = false), 2500);
    }

    save() {
        if (!this.dayReport || this.saving) {
            return;
        }

        if (this.commentControl.invalid) {
            this.commentControl.markAsTouched();

            return;
        }

        this.saving = true;

        const form_data: Record<string, any> = {};
        for (const group of this.groups) {
            for (const { key, field } of group.fields) {
                form_data[key] = field.fc.value;
            }
        }

        const updatedDayReport = {
            ...this.dayReport,
            status_key: this.statusControl.value,
            comment: this.commentControl.value,
            form_data,
        } as Dayreport;

        this.subscriptions.add(
            this.dayreportService.save(updatedDayReport).subscribe(response => {
                this.stateService.updateDayReport(response.data);
                this.saved = true;
                this.saving = false;
                this.markFormAsSaved();
            })
        );
    }

    private setupWeatherAutofill(): void {
        const tempGroup = this.groups.find(g => g.key === 'temp_vochtigheid');
        if (!tempGroup) {
            return;
        }

        const getFC = (key: string) => tempGroup.fields.find(f => f.key === key)?.field.fc;

        ([1, 2] as const).forEach(meting => {
            const wegdekFC = getFC(`vocht_wegdek_${meting}`);
            if (!wegdekFC) {
                return;
            }

            this.subscriptions.add(
                wegdekFC.valueChanges
                    .pipe(
                        debounceTime(300),
                        filter(v => v !== null && v !== '' && v !== undefined),
                        distinctUntilChanged(),
                        filter(() => {
                            const luchtFC = getFC(`vocht_lucht_${meting}`);
                            return !luchtFC?.value || this.autofilledFCs.has(luchtFC);
                        }),
                        switchMap(() => this.weatherService.getCurrentWeather())
                    )
                    .subscribe(response => {
                        const weather = response.data;
                        const round1 = (n: number) => Math.round(n * 10) / 10;

                        const now = new Date();

                        const autofill: [string, any][] = [
                            [`vocht_lucht_${meting}`, round1(weather.temperature.degrees)],
                            [`vocht_luchtvochtigheid_${meting}`, Math.round(weather.relativeHumidity)],
                            [`vocht_dauwpunt_${meting}`, round1(weather.dewPoint.degrees)],
                            [`vocht_tijd_${meting}`, now],
                        ];

                        for (const [key, value] of autofill) {
                            const fc = getFC(key);
                            if (fc) {
                                fc.setValue(value);
                                fc.disable();
                                this.autofilledFCs.add(fc);
                            }
                        }
                    })
            );
        });
    }

    unlockFields(fcs: FormControl[]): void {
        fcs.forEach(fc => {
            fc.enable();
            this.autofilledFCs.delete(fc);
        });
    }

    private setupThermoTijdAutofill(): void {
        const thermoGroup = this.groups.find(g => g.key === 'temp_thermoplast');
        if (!thermoGroup) {
            return;
        }

        const getFC = (key: string) => thermoGroup.fields.find(f => f.key === key)?.field.fc;

        ([1, 2] as const).forEach(meting => {
            const triggerKeys = [`thermo_moederketel_${meting}`, `thermo_machine_${meting}`, `thermo_slof_${meting}`];

            triggerKeys.forEach(triggerKey => {
                const fc = getFC(triggerKey);
                if (!fc) {
                    return;
                }

                this.subscriptions.add(
                    fc.valueChanges
                        .pipe(
                            debounceTime(300),
                            filter(v => v !== null && v !== '' && v !== undefined),
                            distinctUntilChanged(),
                            filter(() => !getFC(`thermo_tijd_${meting}`)?.value)
                        )
                        .subscribe(() => {
                            const tijdFC = getFC(`thermo_tijd_${meting}`);
                            if (tijdFC) {
                                const now = new Date();
                                tijdFC.setValue(now);
                                tijdFC.disable();
                                this.autofilledFCs.add(tijdFC);
                            }
                        })
                );
            });
        });
    }

    private buildGroups(dayReportFields: DayReportFieldGroups): DayReportFormGroup[] {
        return Object.entries(dayReportFields).map(([groupKey, group]) => {
            const fields: DayReportFieldEntry[] = Object.entries(group.fields)
                .sort(([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map(([fieldKey, entityField]) => {
                    return {
                        key: fieldKey,
                        field: {
                            ...entityField,
                            fc: new FormControl(entityField.default ?? null, this.getValidators(entityField)),
                        } as EntityFormField,
                    };
                });

            const entries1 = fields.filter(e => e.key.endsWith('_1'));
            const entries2 = fields.filter(e => e.key.endsWith('_2'));

            const pairs: DayReportFieldPair[] = entries1
                .map(e1 => {
                    const baseKey = e1.key.slice(0, -2);
                    const e2 = entries2.find(e => e.key === baseKey + '_2');
                    if (!e2) return null;
                    return {
                        label: e1.field.name,
                        entry1: e1,
                        entry2: e2,
                    };
                })
                .filter((p): p is DayReportFieldPair => p !== null);

            const isPaired = pairs.length > 0 && pairs.length * 2 === fields.length;

            return {
                key: groupKey,
                label: group.label,
                fields,
                pairs: isPaired ? pairs : null,
            };
        });
    }

    private getValidators(entityField: any): ValidatorFn[] {
        const v = entityField.validators;

        return [
            v?.min !== undefined ? Validators.min(v.min) : null,
            v?.max !== undefined ? Validators.max(v.max) : null,
            v?.minLength !== undefined ? Validators.minLength(v.minLength) : null,
            v?.maxLength !== undefined ? Validators.maxLength(v.maxLength) : null,
            v?.pattern !== undefined ? Validators.pattern(v.pattern) : null,
            entityField.required ? Validators.required : null,
        ].filter((validator): validator is ValidatorFn => validator !== null);
    }
}
